// A rich body editor: an ordered list of text / image / video / link blocks,
// with an animated [+] on the divider between any two of them.
//
// The interaction model is EngTourCreator's edit-stop editor, deliberately —
// somebody who has built a tour should not have to learn a second editor to
// build an event. It is a fresh component rather than an import from that file
// because EngTourCreator's version is welded to the tour: its palette, its
// upload endpoint, its serialize-to-`[[img|src]]` string format. Lifting it out
// would mean editing a working tool to no benefit for it. What is shared is the
// behaviour, which is the part people actually recognise.
//
// Blocks here stay STRUCTURED objects rather than EngTour's bracketed-string
// encoding — events are stored as JSON and normalised server-side, so there is
// no reason to flatten them to text and parse them back.
//
//   { type: 'text',  text }
//   { type: 'image', url, caption?, height?, focus? }   height = crop px, focus = 0-100 %
//   { type: 'video', url, autoplay? }
//   { type: 'link',  url, text }
//
// `onUpload(file) => Promise<url>` is supplied by the host, so this component
// never knows where images go.
//
// Two OPT-IN props exist for the remote-popups editor (PopupsAdmin.js) and
// change nothing for events or tours unless passed:
//   `types`      which block types the [+] menu offers — the phone app has no
//                video player, so the popup editor passes the other three;
//   `textTools`  a small style / alignment toolbar on each text block, writing
//                `style` ('body' | 'small' | 'heading') and `align` ('left' |
//                'center') onto the block, exactly as docs/POPUPS.md names them.
import { useState, useRef, useEffect, useCallback } from 'react';

const BLOCK_TYPES = [
  { type: 'text', emoji: '¶', label: 'text' },
  { type: 'image', emoji: '🖼', label: 'image' },
  { type: 'video', emoji: '🎬', label: 'video' },
  { type: 'link', emoji: '🔗', label: 'link' },
];

let seq = 0;
const nextKey = () => `b${++seq}`;

export function makeBlock(type) {
  if (type === 'link') return { _k: nextKey(), type: 'link', text: 'Learn more', url: '' };
  if (type === 'image') return { _k: nextKey(), type: 'image', url: '', caption: '' };
  if (type === 'video') return { _k: nextKey(), type: 'video', url: '', autoplay: false };
  return { _k: nextKey(), type: 'text', text: '' };
}

/** Give stored blocks the stable keys React needs without touching the data. */
export const withKeys = (blocks) =>
  (blocks || []).map(b => ({ ...b, _k: b._k || nextKey() }));

/** Strip editor-only fields on the way out. */
export const stripKeys = (blocks) =>
  (blocks || []).map(({ _k, ...rest }) => rest);

/** A YouTube id from any of the URL shapes people paste. */
export function youtubeId(url) {
  const s = String(url || '');
  const m = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

/** A textarea that grows with its content — a body block should never scroll. */
function AutoTextarea({ value, onChange, placeholder, className }) {
  const ref = useRef(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useEffect(fit, [value, fit]);
  return (
    <textarea ref={ref} className={className} value={value} placeholder={placeholder}
              rows={1} onChange={e => { onChange(e.target.value); fit(); }} />
  );
}

/**
 * The floating URL editor, centred ON the block it belongs to.
 *
 * Centred rather than inserted below because editing a link must not move the
 * column: a panel that pushes the page down as it opens makes you lose the
 * block you were looking at, every time.
 */
function UrlOverlay({ icon, title, value, placeholder, onChange, onClose, dim, text, onText }) {
  const hasText = onText !== undefined;
  return (
    <>
      <div className={`be-scrim ${dim ? 'dim' : ''}`} onClick={onClose} />
      <div className="be-overlay" onClick={e => e.stopPropagation()}>
        {hasText && (
          <>
            <div className="be-olabel">✎ BUTTON TEXT</div>
            <input className="be-oinput" autoFocus value={text} placeholder="Button label"
                   onChange={e => onText(e.target.value)}
                   onKeyDown={e => { if (e.key === 'Enter') onClose(); }} />
          </>
        )}
        <div className="be-olabel">{icon} {title}</div>
        <div className="be-orow">
          <input className="be-oinput" autoFocus={!hasText} value={value} placeholder={placeholder}
                 onChange={e => onChange(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') onClose(); }} />
          <button className="be-ok" onClick={onClose} title="Done">✓</button>
        </div>
      </div>
    </>
  );
}

function EditPill({ label, onClick }) {
  return <button className="be-pill" onClick={onClick}>{label}</button>;
}

/**
 * Is this block's URL editor open?
 *
 * A block with no URL yet opens its editor immediately — but as INITIAL state,
 * never as a live `editing || !block.url`. That expression closed the overlay on
 * the first keystroke: typing one character made `block.url` truthy, which
 * flipped the condition false and tore the panel down mid-word, which reads as
 * the field having submitted itself. Once open, only Done or a click away closes
 * it.
 */
function useUrlEditor(block) {
  const [editing, setEditing] = useState(() => !block.url);
  return [editing, setEditing];
}

function LinkBlock({ block, hover, onChange }) {
  const [open, setOpen] = useUrlEditor(block);
  return (
    <div className="be-rel">
      <div className={`be-linkbox ${hover ? 'hot' : ''}`}>
        <input value={block.text} placeholder="Button label"
               onChange={e => onChange({ text: e.target.value })} />
      </div>
      {hover && !open && <EditPill label="🔗 Edit link" onClick={() => setOpen(true)} />}
      {open && (
        <UrlOverlay icon="🔗" title="LINK URL" value={block.url} placeholder="https://…"
                    text={block.text} onText={v => onChange({ text: v })}
                    onChange={v => onChange({ url: v })} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}

export function VideoEmbed({ url, autoplay = false }) {
  const id = youtubeId(url);
  if (id) {
    const src = `https://www.youtube.com/embed/${id}${autoplay ? '?autoplay=1&mute=1' : ''}`;
    return (
      <div className="be-ratio">
        <iframe src={src} title="Video" frameBorder="0" allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
      </div>
    );
  }
  if (!url) return null;
  return <video src={url} controls muted={autoplay} playsInline style={{ width: '100%', display: 'block' }} />;
}

function VideoBlock({ block, hover, onChange }) {
  const [open, setOpen] = useUrlEditor(block);
  return (
    <div className="be-rel">
      {block.url
        ? <div className="be-video"><VideoEmbed url={block.url} /></div>
        /* Clickable, because the overlay can now be dismissed with the URL still
           empty — this is the way back to it. */
        : <div className="be-drop" onClick={() => setOpen(true)}>🎬 Add a video</div>}
      {block.url && (
        <button className={`be-toggle ${block.autoplay ? 'on' : ''}`}
                onClick={() => onChange({ autoplay: !block.autoplay })}
                title="Start this video (muted) when the event page opens">
          {block.autoplay ? '▶ Auto-play: ON' : '▶ Auto-play: OFF'}
        </button>
      )}
      {hover && !open && <EditPill label="🎬 Edit link" onClick={() => setOpen(true)} />}
      {open && (
        <UrlOverlay icon="🎬" title="VIDEO URL" value={block.url} placeholder="YouTube or mp4 URL"
                    onChange={v => onChange({ url: v })} onClose={() => setOpen(false)} dim={!!block.url} />
      )}
    </div>
  );
}

const pct = (v, fallback) => (Number.isFinite(v) ? v : fallback);

/** How wide the picture is, as a percentage of the column. 100 = full width. */
export function imgWidthPct(block) {
  const w = Number(block?.width);
  return Number.isFinite(w) && w > 0 && w < 100 ? w : 100;
}

/**
 * The FRAME a cropped image sits in, in ONE place.
 *
 * Split from `imgStyle` because the width belongs to the box, not the picture:
 * the editor hangs its grips on the frame's own edges, and a grip anchored to
 * the full column would float in space beside a half-width photo. A narrow
 * picture is centred — one hugging the left margin reads as a layout bug rather
 * than as somebody's decision.
 */
export function imgFrameStyle(block) {
  const w = imgWidthPct(block);
  if (w >= 100) return undefined;
  return { width: `${w}%`, marginLeft: 'auto', marginRight: 'auto' };
}

/**
 * A cropped image's own CSS, in ONE place.
 *
 * Exported because EventBody renders the same blocks read-only, and a crop that
 * looks one way while being authored and another way to an attendee is the
 * failure this whole editor exists to avoid.
 *
 * `objectPosition` carries BOTH axes: once a photo is narrower than the column
 * and shorter than itself, `cover` is throwing away sides as well as top and
 * bottom, and which sides is a decision only the author can make.
 */
export function imgStyle(block) {
  if (!block?.height) return undefined;
  return {
    width: '100%',
    height: `${block.height}px`,
    objectFit: 'cover',
    objectPosition: `${pct(block.focusX, 50)}% ${pct(block.focus, 50)}%`,
  };
}

/** One pointer drag, wired to the window so it survives leaving the element. */
function drag(e, onMove, onDone) {
  e.preventDefault(); e.stopPropagation();
  const move = (ev) => onMove(ev);
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    onDone?.();
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

const clamp = (v, lo, hi) => Math.round(Math.max(lo, Math.min(hi, v)));

/**
 * Click-or-drop image well, with crop grips on the bottom and right edges.
 *
 * The same model as the Eng Tour's stop editor: the picture stays where it is
 * and the FRAME around it moves, with `object-fit: cover` inside. Nothing is
 * re-encoded and the stored crop is three numbers, so it is entirely
 * reversible — a photo cropped tight can be opened back up months later.
 *
 * It cropped vertically only, on the reasoning that a body image is as wide as
 * the column whatever you do. That is wrong often enough to matter: a poster, a
 * phone screenshot and a portrait photo all look absurd blown across the full
 * width, and the only fix available was to leave them out. So the right edge is
 * a grip too, and the corner drags both at once. Dragging the picture itself
 * pans it inside whatever frame is left, on both axes, because once `cover` is
 * discarding sides as well as top and bottom, which sides is a real decision.
 *
 * Width is stored as a PERCENTAGE of the column rather than pixels: the event
 * page is read on a phone and in a desktop popup, and a picture pinned to 380px
 * would be half the column in one and overflow the other.
 */
function ImageBlock({ block, onChange, onUpload }) {
  const fileRef = useRef(null);
  const colRef = useRef(null);     // the full column, for the width arithmetic
  const frameRef = useRef(null);   // the picture's own box
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [over, setOver] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [panning, setPanning] = useState(false);

  const take = async (file) => {
    if (!file) return;
    setErr(''); setBusy(true);
    // A replacement is a different picture: keeping the previous crop would
    // apply numbers chosen for one image to another they mean nothing for.
    try { onChange({ url: await onUpload(file), width: null, height: null, focus: null, focusX: null }); }
    catch (e) { setErr(e?.message || 'Upload failed.'); }
    setBusy(false);
  };

  const cropped = block.height != null || (block.width != null && block.width < 100);

  // Pointer events rather than mouse: the same handler then works for a
  // trackpad and a touchscreen.
  //
  // `axis` is 'y' (bottom edge), 'x' (right edge) or 'xy' (the corner). The
  // width doubles the horizontal delta because the picture is centred — both
  // margins give way at once, so the edge under the finger moves half as far as
  // the width changes, and without the doubling the grip drifts away from the
  // pointer as you drag.
  const startResize = (axis) => (e) => {
    const startX = e.clientX, startY = e.clientY;
    const startH = block.height || frameRef.current?.offsetHeight || 220;
    const startW = imgWidthPct(block);
    const col = colRef.current?.offsetWidth || 1;
    setResizing(true);
    drag(e, (ev) => {
      const next = {};
      if (axis !== 'x') next.height = clamp(startH + (ev.clientY - startY), 80, 720);
      if (axis !== 'y') next.width = clamp(startW + ((ev.clientX - startX) / col) * 200, 20, 100);
      onChange(next);
    }, () => setResizing(false));
  };

  // Drag the picture to choose which part of it the crop keeps. Only meaningful
  // once a height has been set, since an uncropped image shows all of itself.
  const startPan = (e) => {
    if (!block.height) return;
    const startX = e.clientX, startY = e.clientY;
    const startFY = pct(block.focus, 50);
    const startFX = pct(block.focusX, 50);
    const boxH = frameRef.current?.offsetHeight || 220;
    const boxW = frameRef.current?.offsetWidth || 220;
    setPanning(true);
    drag(e, (ev) => {
      // A full drag across the frame sweeps the whole picture past it.
      onChange({
        focus: clamp(startFY - ((ev.clientY - startY) / Math.max(40, boxH)) * 100, 0, 100),
        focusX: clamp(startFX - ((ev.clientX - startX) / Math.max(40, boxW)) * 100, 0, 100),
      });
    }, () => setPanning(false));
  };

  return (
    <div className="be-rel" ref={colRef}>
      {block.url ? (
        /* The frame carries the width, so the grips hang on the PICTURE's own
           edges. They used to be positioned against this whole block — which
           includes the tools row underneath — so the bottom grip sat across the
           Replace and Reset crop buttons and swallowed every click on them. */
        <div className="be-imgframe" ref={frameRef} style={imgFrameStyle(block)}>
          <img className={`be-img ${block.height ? 'cropped' : ''} ${panning ? 'panning' : ''}`}
               src={block.url} alt="" draggable={false}
               onPointerDown={startPan}
               style={imgStyle(block)} />
          {/* Each grip sits ON the edge it moves. */}
          <div className="be-grip be-grip-b" onPointerDown={startResize('y')}
               title="Drag to crop the height"><span /></div>
          <div className="be-grip be-grip-r" onPointerDown={startResize('x')}
               title="Drag to narrow the picture"><span /></div>
          <div className="be-grip be-grip-c" onPointerDown={startResize('xy')}
               title="Drag to crop both ways" />
          {(resizing || panning || cropped) && (
            <div className="be-cropbadge">
              {imgWidthPct(block)}% wide{block.height ? ` · ${block.height}px tall` : ''}
              {block.height ? ' · drag the picture to pan' : ''}
            </div>
          )}
        </div>
      ) : (
        <div className={`be-drop ${over ? 'over' : ''}`}
             onClick={() => fileRef.current?.click()}
             onDragOver={e => { e.preventDefault(); setOver(true); }}
             onDragLeave={() => setOver(false)}
             onDrop={e => { e.preventDefault(); setOver(false); take(e.dataTransfer.files?.[0]); }}>
          {busy ? 'Uploading…' : '🖼 Click or drop an image'}
        </div>
      )}
      {block.url && (
        <div className="be-imgtools">
          <button className="be-mini" onClick={() => fileRef.current?.click()}>Replace</button>
          {cropped && (
            <button className="be-mini"
                    onClick={() => onChange({ width: null, height: null, focus: null, focusX: null })}>
              Reset crop
            </button>
          )}
          <input className="be-caption" value={block.caption || ''} placeholder="Caption (optional)"
                 onChange={e => onChange({ caption: e.target.value })} />
        </div>
      )}
      {err && <div className="be-err">{err}</div>}
      <input ref={fileRef} type="file" accept="image/*" hidden
             onChange={e => { take(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
}

/**
 * The divider between two blocks. Hovering animates a [+] in; clicking it opens
 * the type menu. Once open the menu STAYS open until a choice or a click away —
 * the gap between the [+] and the menu would otherwise drop hover and close it
 * out from under the cursor.
 */
function InsertDivider({ onInsert, types }) {
  const [hover, setHover] = useState(false);
  const [menu, setMenu] = useState(false);
  const open = hover || menu;
  const offered = types ? BLOCK_TYPES.filter(t => types.includes(t.type)) : BLOCK_TYPES;
  return (
    <div className={`be-div ${open ? 'open' : ''}`}
         onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="be-divline" />
      <button className="be-plus" title="Insert here" onClick={() => setMenu(m => !m)}>+</button>
      {menu && (
        <>
          <div className="be-clickaway" onClick={() => { setMenu(false); setHover(false); }} />
          <div className="be-menu">
            {offered.map(t => (
              <button key={t.type} onClick={() => { onInsert(t.type); setMenu(false); setHover(false); }}>
                <span className="be-memoji">{t.emoji}</span>
                <span className="be-melabel">Add {t.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function BlockEditor({ blocks, onChange, onUpload, types, textTools = false }) {
  const list = blocks || [];
  const patch = (i, fields) => onChange(list.map((b, k) => (k === i ? { ...b, ...fields } : b)));
  const insert = (i, type) => onChange([...list.slice(0, i), makeBlock(type), ...list.slice(i)]);
  const remove = (i) => onChange(list.filter((_, k) => k !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="be-root">
      <style>{CSS}</style>
      <InsertDivider onInsert={t => insert(0, t)} types={types} />
      {list.map((b, i) => (
        <div key={b._k || i}>
          <Block block={b} index={i} count={list.length} onUpload={onUpload} textTools={textTools}
                 onChange={f => patch(i, f)} onRemove={() => remove(i)} onMove={d => move(i, d)} />
          <InsertDivider onInsert={t => insert(i + 1, t)} types={types} />
        </div>
      ))}
      {list.length === 0 && (
        <div className="be-hint">
          Nothing here yet. Use the <strong>+</strong> above to add a paragraph, a photo
          {types && !types.includes('video') ? '' : ', a video'} or a link — this is what{' '}
          {types ? 'readers' : 'attendees'} see when they open {types ? 'it' : 'the event'}.
        </div>
      )}
    </div>
  );
}

/** The opt-in text toolbar: style, then alignment. Writes the contract's values. */
const TEXT_STYLES = [['body', 'Body'], ['small', 'Small'], ['heading', 'Heading']];
const TEXT_ALIGNS = [['left', 'Left'], ['center', 'Centre']];
function TextTools({ block, onChange }) {
  const style = block.style || 'body';
  const align = block.align || 'left';
  return (
    <div className="be-ttools">
      {TEXT_STYLES.map(([v, label]) => (
        <button key={v} type="button" className={`be-tt ${style === v ? 'on' : ''}`}
                onClick={() => onChange({ style: v === 'body' ? undefined : v })}>{label}</button>
      ))}
      <span className="be-ttsep" />
      {TEXT_ALIGNS.map(([v, label]) => (
        <button key={v} type="button" className={`be-tt ${align === v ? 'on' : ''}`}
                onClick={() => onChange({ align: v === 'left' ? undefined : v })}>{label}</button>
      ))}
    </div>
  );
}

function Block({ block, index, count, onChange, onRemove, onMove, onUpload, textTools }) {
  const [hover, setHover] = useState(false);
  const textClass = block.type === 'text'
    ? `be-text${block.style === 'heading' ? ' be-text-heading' : block.style === 'small' ? ' be-text-small' : ''}${block.align === 'center' ? ' be-text-center' : ''}`
    : '';
  return (
    <div className={`be-block ${hover ? 'hot' : ''}`}
         onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {block.type === 'text' && textTools && <TextTools block={block} onChange={onChange} />}
      {block.type === 'text' && (
        <AutoTextarea className={textClass} value={block.text || ''}
                      placeholder="Write a paragraph…" onChange={v => onChange({ text: v })} />
      )}
      {block.type === 'image' && <ImageBlock block={block} onChange={onChange} onUpload={onUpload} />}
      {block.type === 'video' && <VideoBlock block={block} hover={hover} onChange={onChange} />}
      {block.type === 'link' && <LinkBlock block={block} hover={hover} onChange={onChange} />}

      <button className="be-corner tl" onClick={onRemove} title="Remove">✕</button>
      <button className="be-corner tr" onClick={() => onMove(-1)} disabled={index === 0} title="Move up">↑</button>
      <button className="be-corner br" onClick={() => onMove(1)} disabled={index === count - 1} title="Move down">↓</button>
    </div>
  );
}

const CSS = `
.be-root { --line:#e2e5ea; --accent:#4f46e5; --ink:#1b1e23; --dim:#6b7280; }
.be-hint { color:var(--dim); font-size:13.5px; padding:8px 2px 2px; }
.be-rel { position:relative; }
.be-block { position:relative; margin:2px 0; }
.be-corner { position:absolute; z-index:4; width:25px; height:25px; border-radius:7px;
  background:#fff; box-shadow:0 2px 8px rgba(0,0,0,.22); cursor:pointer; font-size:12px;
  font-weight:800; display:flex; align-items:center; justify-content:center;
  opacity:0; transition:opacity 120ms ease; border:1px solid var(--line); color:#41474f; }
.be-block.hot .be-corner { opacity:1; }
.be-corner:disabled { opacity:.3 !important; cursor:default; }
.be-corner.tl { top:-9px; left:-9px; border-color:#e6bdb7; color:#c0392b; }
.be-corner.tr { top:-9px; right:-9px; }
.be-corner.br { bottom:-9px; right:-9px; }

.be-text { width:100%; box-sizing:border-box; font:inherit; font-size:14.5px; line-height:1.65;
  color:var(--ink); border:1px solid transparent; border-radius:8px; background:transparent;
  padding:5px 7px; resize:none; overflow:hidden; }
.be-text:focus { outline:none; border-color:var(--line); background:#fff; }
.be-text-heading { font-weight:800; font-size:16px; }
.be-text-small { font-size:12.5px; color:var(--dim); }
.be-text-center { text-align:center; }
.be-ttools { display:flex; align-items:center; gap:3px; padding:0 4px 2px; }
.be-tt { border:1px solid var(--line); background:#fff; color:#41474f; border-radius:6px;
  padding:2px 8px; font:inherit; font-size:11px; font-weight:700; cursor:pointer; }
.be-tt.on { border-color:var(--accent); background:var(--accent); color:#fff; }
.be-ttsep { width:1px; height:14px; background:var(--line); margin:0 4px; }

/* The picture's own box. Everything to do with cropping is positioned against
   THIS, not against the block — the block also contains the tools row, and a
   grip pinned to its bottom edge covered the buttons there. */
.be-imgframe { position:relative; line-height:0; }
.be-img { width:100%; display:block; border-radius:12px; }
/* The crop grips and their badge. */
.be-grip { position:absolute; z-index:4; display:flex; align-items:center;
  justify-content:center; touch-action:none; }
.be-grip span { border-radius:3px; background:rgba(255,255,255,.92); }
/* Bottom edge — height. */
.be-grip-b { left:0; right:0; bottom:0; height:22px; cursor:ns-resize;
  align-items:flex-end; padding-bottom:5px; border-radius:0 0 12px 12px;
  background:linear-gradient(to top, rgba(0,0,0,.5), rgba(0,0,0,0)); }
.be-grip-b span { width:48px; height:5px; }
/* Right edge — width. Stops short of the bottom so the two never overlap and
   leave a corner where it is a coin toss which one you grabbed. */
.be-grip-r { top:0; bottom:22px; right:0; width:22px; cursor:ew-resize;
  justify-content:flex-end; padding-right:5px; border-radius:0 12px 0 0;
  background:linear-gradient(to left, rgba(0,0,0,.5), rgba(0,0,0,0)); }
.be-grip-r span { width:5px; height:48px; }
/* The corner — both at once, and the reason the other two can stay single-axis. */
.be-grip-c { right:0; bottom:0; width:22px; height:22px; cursor:nwse-resize;
  border-radius:0 0 12px 0; background:rgba(0,0,0,.5); }
.be-cropbadge { position:absolute; left:8px; bottom:26px; z-index:5;
  background:rgba(0,0,0,.62); color:#fff; font-size:10.5px; font-weight:700;
  line-height:1.5; padding:2px 7px; border-radius:6px; pointer-events:none;
  max-width:calc(100% - 38px); }
.be-img.cropped { cursor:grab; touch-action:none; }
.be-img.panning { cursor:grabbing; }

.be-drop { height:120px; display:flex; align-items:center; justify-content:center;
  border:2px dashed var(--line); border-radius:12px; color:#9aa1ab; font-size:13px;
  font-weight:700; cursor:pointer; transition:border-color 120ms, color 120ms; }
.be-drop.over { border-color:var(--accent); color:var(--accent); }
.be-imgtools { display:flex; gap:8px; align-items:center; margin-top:6px; }
.be-mini { border:1px solid var(--line); background:#fff; border-radius:7px; padding:4px 10px;
  font:inherit; font-size:12px; cursor:pointer; flex:none; }
.be-caption { flex:1; min-width:0; border:1px solid var(--line); border-radius:7px;
  padding:5px 9px; font:inherit; font-size:12.5px; }
.be-err { color:#b91c1c; font-size:12px; margin-top:5px; }

.be-video { border-radius:12px; overflow:hidden; background:#000; }
.be-ratio { position:relative; padding-top:56.25%; }
.be-ratio iframe { position:absolute; inset:0; width:100%; height:100%; border:0; }
.be-toggle { margin-top:6px; display:inline-flex; align-items:center; gap:6px; padding:5px 11px;
  border-radius:999px; border:1.5px solid var(--line); background:#fff; color:#41474f;
  font:inherit; font-size:12px; font-weight:800; cursor:pointer; }
.be-toggle.on { border-color:var(--accent); background:var(--accent); color:#fff; }

.be-linkbox { background:#eef0fb; border-radius:11px; padding:10px 14px; display:flex;
  justify-content:center; outline:2px solid transparent; transition:outline-color 120ms; }
.be-linkbox.hot { outline-color:var(--accent); }
.be-linkbox input { border:none; outline:none; background:transparent; text-align:center;
  color:#3730a3; font:inherit; font-weight:800; font-size:14px; width:100%; }

.be-pill { position:absolute; top:8px; left:50%; transform:translateX(-50%); z-index:5;
  background:rgba(31,35,45,.9); color:#fff; border:none; border-radius:999px; padding:5px 12px;
  font:inherit; font-size:11px; font-weight:800; cursor:pointer; white-space:nowrap;
  box-shadow:0 2px 8px rgba(0,0,0,.3); }

.be-scrim { position:absolute; inset:0; z-index:6; border-radius:12px; background:transparent; }
.be-scrim.dim { background:rgba(20,30,45,.5); }
.be-overlay { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); z-index:7;
  width:min(86%,320px); background:#fff; border-radius:10px; padding:11px 13px;
  box-shadow:0 8px 26px rgba(0,0,0,.35); border:1px solid var(--line); }
.be-olabel { font-size:9px; font-weight:800; letter-spacing:.08em; color:#9aa1ab; margin:0 0 5px; }
.be-orow { display:flex; gap:6px; }
.be-oinput { width:100%; box-sizing:border-box; font:inherit; font-size:12.5px; padding:7px 9px;
  border:1px solid var(--line); border-radius:7px; outline:none; background:#fff; margin-bottom:8px; }
.be-orow .be-oinput { flex:1; min-width:0; margin-bottom:0; }
.be-ok { border:none; background:var(--accent); color:#fff; border-radius:7px; padding:0 12px;
  cursor:pointer; font-weight:800; font-size:13px; }

.be-div { position:relative; height:16px; transition:height 120ms ease; }
.be-div.open { height:30px; }
.be-divline { position:absolute; top:50%; left:0; right:0; border-top:1px dashed var(--line);
  transition:border-color 120ms; }
.be-div.open .be-divline { border-color:var(--accent); }
.be-plus { position:absolute; top:50%; left:50%; z-index:16;
  transform:translate(-50%,-50%) scale(.55); opacity:.28;
  transition:transform 130ms ease, opacity 130ms ease;
  width:24px; height:24px; border-radius:7px; border:none; cursor:pointer;
  background:var(--accent); color:#fff; font-size:16px; font-weight:800; line-height:1;
  display:flex; align-items:center; justify-content:center; box-shadow:0 2px 8px rgba(0,0,0,.25); }
.be-div.open .be-plus { transform:translate(-50%,-50%) scale(1); opacity:1; }
.be-clickaway { position:fixed; inset:0; z-index:14; }
.be-menu { position:absolute; top:calc(50% + 14px); left:50%; transform:translateX(-50%);
  z-index:15; background:#fff; border-radius:10px; padding:6px; display:flex; gap:4px;
  box-shadow:0 6px 20px rgba(0,0,0,.25); border:1px solid var(--line); }
.be-menu button { display:flex; flex-direction:column; align-items:center; gap:2px;
  padding:6px 10px; border:none; background:transparent; cursor:pointer; border-radius:7px;
  color:var(--ink); font:inherit; }
.be-menu button:hover { background:#f1f2f6; }
.be-memoji { font-size:16px; }
.be-melabel { font-size:10px; font-weight:800; }
`;
